import ReactSelect, { Props as ReactSelectProps, StylesConfig, GroupBase } from 'react-select';

export interface SelectOption {
  label: string;
  value: string | number;
}

interface SimpleSelectProps extends Omit<ReactSelectProps<SelectOption, boolean, GroupBase<SelectOption>>, 'onChange' | 'value'> {
  value?: string | number;
  onChange: (value: string | number | null) => void;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  isSearchable?: boolean;
}

const customStyles: StylesConfig<SelectOption, boolean> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.1)' : 'none',
    '&:hover': {
      borderColor: '#4f46e5',
    },
    minHeight: '42px',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#4f46e5'
      : state.isFocused
      ? '#eef2ff'
      : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.875rem',
    padding: '0.5rem 0.75rem',
    '&:active': {
      backgroundColor: '#4338ca',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 50,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.25rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1f2937',
    fontSize: '0.875rem',
  }),
};

export default function SimpleSelect({
  value,
  onChange,
  label,
  error,
  required,
  helperText,
  isSearchable = true,
  options,
  placeholder = 'Select...',
  ...rest
}: SimpleSelectProps) {
  // Find selected option object from value
  const selectedOption = options?.find((option: any) => option.value === value) || null;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <ReactSelect
        {...rest}
        value={selectedOption}
        onChange={(option: any) => {
          onChange(option?.value || null);
        }}
        options={options}
        styles={customStyles}
        classNamePrefix="react-select"
        className="react-select-container"
        isSearchable={isSearchable}
        placeholder={placeholder}
        menuPortalTarget={document.body}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      {helperText && !error && <p className="text-gray-500 text-xs mt-1">{helperText}</p>}
    </div>
  );
}
